import { getOperatorConfig } from '@modules/src/operatorConfig'
import type { PreloaderBrandingRule } from '@modules/src/operatorConfig'

// Lazily-loaded registry of preloader brand logo assets.
// Every {name}.json + {name}.png pair dropped into this folder is auto-discovered —
// no per-brand code needed. Only the pair actually requested gets fetched (Vite
// code-splits each match into its own chunk since these globs are not `eager`).
const atlasLoaders = import.meta.glob('./*.json') as Record<string, () => Promise<{ default: unknown }>>
const imageLoaders = import.meta.glob('./*.png') as Record<string, () => Promise<{ default: string }>>

const keyFromPath = (path: string): string => path.replace(/^.*\//, '').replace(/\.(json|png)$/i, '')

const findLoader = <T>(loaders: Record<string, () => Promise<{ default: T }>>, key: string) => {
    const path = Object.keys(loaders).find((candidate) => keyFromPath(candidate) === key)
    return path ? loaders[path] : null
}

export interface IBrandLogoAssets {
    image: string
    atlas: unknown
}

type TAtlasLike = { frames?: Record<string, unknown>; [key: string]: unknown }

// Some brands share one packed atlas (e.g. all_brand_logos.json holds both
// Onyx's and Arizona's static logo, each as its own frame). Narrowing to only
// the frames whose key starts with the rule's spriteName keeps the renderer
// (which animates through every frame it's given) from flipping between
// unrelated brands' frames. This is a no-op for a dedicated per-brand atlas
// (e.g. fliff's) since every frame there already shares that same prefix.
const filterAtlasFrames = (atlas: unknown, frameKeyPrefix?: string): unknown => {
    if (!frameKeyPrefix) return atlas

    const source = atlas as TAtlasLike
    if (!source?.frames) return atlas

    const frames = Object.fromEntries(Object.entries(source.frames).filter(([key]) => key.startsWith(frameKeyPrefix)))

    return { ...source, frames }
}

/**
 * Loads a brand's logo image + atlas pair by filename (with or without extension),
 * e.g. loadBrandLogoAssets('fliff_brandlogo01') or ('fliff_brandlogo01.json').
 * Pass frameKeyPrefix when several brands share one atlas file, to narrow it
 * down to just that brand's frame(s) — see filterAtlasFrames above.
 * Returns null if no matching pair exists in this folder.
 */
export const loadBrandLogoAssets = async (
    spriteFileName: string,
    frameKeyPrefix?: string
): Promise<IBrandLogoAssets | null> => {
    const key = keyFromPath(spriteFileName)

    const loadAtlas = findLoader(atlasLoaders, key)
    const loadImage = findLoader(imageLoaders, key)

    if (!loadAtlas || !loadImage) {
        console.warn(`[brandLogos] No brand logo assets found for "${spriteFileName}"`)
        return null
    }

    const [atlasModule, imageModule] = await Promise.all([loadAtlas(), loadImage()])
    return { atlas: filterAtlasFrames(atlasModule.default, frameKeyPrefix), image: imageModule.default }
}

const normalizeCode = (value?: string | null): string => String(value || '').trim().toUpperCase()

const inferNetworkCode = (siteCode: string): string => (siteCode.length >= 5 ? siteCode.slice(2, 5).toUpperCase() : '')

const findBrandingRule = (
    rules: PreloaderBrandingRule[],
    scope: PreloaderBrandingRule['scope'],
    code: string,
    layer: number
): PreloaderBrandingRule | undefined => rules.find((rule) => rule.scope === scope && rule.code === code && rule.layer === layer)

/**
 * Resolves which preloader branding rule applies for a given site/network, so
 * brand assets can be looked up dynamically instead of hardcoding a site-code
 * list per brand. Onboarding a new brand only needs a new entry in
 * preloaderBrandingRules (public/assets/js/operatorConfig.js) plus its asset
 * pair dropped in this folder — no code changes here.
 *
 * NOTE: this mirrors resolvePreloaderBrandingRule() added to
 * packages/modules/src/operatorConfig/resolver.ts. It's duplicated here
 * because this game pins a prebuilt @dragonframework/modules package that
 * hasn't been rebuilt/republished with that export yet. Once it has, this
 * local copy can be deleted in favor of importing the shared one.
 */
export const resolvePreloaderBrandingRule = (
    siteCode?: string | null,
    networkCode?: string | null,
    layer: number = 1
): PreloaderBrandingRule | null => {
    const rules = getOperatorConfig().preloaderBrandingRules || []
    const site = normalizeCode(siteCode)
    const network = normalizeCode(networkCode) || inferNetworkCode(site)

    let rule =
        (site && findBrandingRule(rules, 'site', site, layer)) ||
        (network && findBrandingRule(rules, 'network', network, layer)) ||
        findBrandingRule(rules, 'default', '*', layer) ||
        null

    const seenAliases = new Set<string>()
    while (rule?.sameAs && !seenAliases.has(rule.sameAs)) {
        seenAliases.add(rule.sameAs)
        const aliased = findBrandingRule(rules, rule.scope, rule.sameAs, layer)
        if (!aliased) break
        rule = aliased
    }

    return rule
}
